import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ToastContainer, useToast } from '../../components/Toast';
import { componentsApi, pagesApi } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useIsMimicking } from './useIsMimicking';
import { useCanUseRoary } from '../../hooks/useCanUseRoary';
import { usePageEditorState } from './usePageEditorState';
import { createPageEditorHandlers } from './usePageEditorHandlers';
import { PageEditorNav } from './PageEditorNav';
import { PageEditorContent } from './PageEditorContent';
import { PageEditorModals } from './PageEditorModals';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import type { Component, LibraryComponent } from '../../schemas/validation';
import { ComponentEditDraftProvider } from '../../contexts/ComponentEditDraftContext';
import { useUnpublishedSummary } from '../../hooks/useUnpublishedSummary';
import { GettingStartedHost, markEditedPage } from '../../components/GettingStarted';

export const PageEditor: React.FC = () => {
  const { siteId, pageId } = useParams<{ siteId: string; pageId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMimicking = useIsMimicking();
  const { isAdmin } = useAuth();
  const canUseRoaryUi = useCanUseRoary();
  const { toasts, showToast, removeToast } = useToast(canUseRoaryUi);
  const state = usePageEditorState();

  const handlers = createPageEditorHandlers({
    siteId,
    pageId,
    navigate,
    state,
    showToast,
  });

  const { pageStatusMap, refetch: refetchPublishStatus } = useUnpublishedSummary(siteId);
  const currentPagePublishStatus = pageId ? pageStatusMap[pageId] : undefined;

  // Library editor modal state (kept local as it's only used for modal)
  const [libraryEditorModal, setLibraryEditorModal] = React.useState<{
    isOpen: boolean;
    component: LibraryComponent | null;
  }>({ isOpen: false, component: null });

  // Responsive preview, SEO analysis, and Media Library state (kept local)
  const [showResponsivePreview, setShowResponsivePreview] = React.useState(false);
  const [showSEOAnalysis, setShowSEOAnalysis] = React.useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = React.useState(false);
  const [showRoaryBanner, setShowRoaryBanner] = React.useState(false);

  React.useEffect(() => {
    if (searchParams.get('roary') !== '1') return;
    setShowRoaryBanner(true);
    const next = new URLSearchParams(searchParams);
    next.delete('roary');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Initial page data load
  useEffect(() => {
    if (!siteId || !pageId) {
      navigate('/dashboard');
      return;
    }

    handlers.loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, pageId, navigate]);

  // Watch for theme or brand package changes and reload colors
  const siteBrandPackageId = (state.site as any)?.brandPackageId;
  useEffect(() => {
    // Reload colors when theme or brand package changes
    if (state.site?.theme || siteBrandPackageId) {
      handlers.loadThemeColors(state.site?.theme || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.site?.theme, siteBrandPackageId]);

  // Load components when refresh trigger changes
  useEffect(() => {
    if (pageId && state.componentRefreshTrigger > 0) {
      componentsApi
        .getPageComponents(pageId)
        .then(data => state.setComponents(data.components))
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, state.componentRefreshTrigger]);

  // Refresh page metadata after a component is saved so the publish badge updates
  const handleComponentSaved = useCallback(async () => {
    if (!pageId) return;
    markEditedPage();
    try {
      const freshPage = await pagesApi.get(pageId);
      state.setCurrentPage(freshPage);
      await refetchPublishStatus();
    } catch (err) {
      console.error('Failed to refresh page after component save:', err);
    }
  }, [pageId, state, refetchPublishStatus]);

  // Keep PageEditor's components state in sync with ComponentManager mutations
  // so the preview panel (and any other consumer) sees changes immediately.
  const handleComponentsChanged = useCallback(
    (updatedComponents: Component[]) => {
      state.setComponents(updatedComponents);
    },
    [state]
  );

  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading page editor...</p>
        </div>
      </div>
    );
  }

  if (!state.site || !state.currentPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Page not found</h2>
          <p className="mt-2 text-gray-600">
            The page you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link to={`/sites/${siteId}`} className="mt-4 btn-primary inline-block">
            Back to Website
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ComponentEditDraftProvider>
      <div className={`min-h-screen bg-gray-50 ${isMimicking ? 'mt-[52px]' : ''}`}>
        <PageEditorNav
          site={state.site}
          currentPage={state.currentPage}
          siteId={siteId!}
          pageId={pageId!}
          sitePages={state.sitePages}
          isSavingTemplate={state.isSavingTemplate}
          isPublishing={state.isPublishing}
          pagePublishStatus={currentPagePublishStatus}
          onShowSaveTemplateModal={() => state.setShowSaveTemplateModal(true)}
          onShowEditPageModal={() => state.setShowEditPageModal(true)}
          onShowResponsivePreview={() => setShowResponsivePreview(true)}
          onShowSEOAnalysis={() => setShowSEOAnalysis(true)}
          onShowMediaLibrary={() => setShowMediaLibrary(true)}
          onPublish={async () => {
            await handlers.handlePublish();
            await refetchPublishStatus();
          }}
        />

        <PageEditorContent
          siteId={siteId!}
          pageId={pageId!}
          pageSlug={state.currentPage.slug ?? ''}
          site={state.site}
          components={state.components}
          pageLayout={state.pageLayout}
          themeColors={state.themeColors}
          themeError={state.themeError}
          isAdmin={isAdmin}
          componentRefreshTrigger={state.componentRefreshTrigger}
          onAddComponent={() => state.setShowComponentLibraryModal(true)}
          onAddComponentAt={handlers.handleAddComponentAt}
          onEditInvisibleComponent={handlers.handleEditInvisibleComponent}
          onDeleteInvisibleComponent={handlers.handleDeleteInvisibleComponent}
          onAddInvisibleComponent={handlers.handleAddInvisibleComponent}
          onClearThemeError={() => state.setThemeError(null)}
          onComponentSaved={handleComponentSaved}
          onComponentsChanged={handleComponentsChanged}
          showRoaryBanner={showRoaryBanner}
          onDismissRoaryBanner={() => setShowRoaryBanner(false)}
        />

        <PageEditorModals
          siteId={siteId!}
          pageId={pageId!}
          site={state.site}
          currentPage={state.currentPage}
          components={state.components}
          isAdmin={isAdmin}
          // Modal visibility
          showSaveTemplateModal={state.showSaveTemplateModal}
          showComponentLibraryModal={state.showComponentLibraryModal}
          showHeadContentModal={state.showHeadContentModal}
          showBodyEndModal={state.showBodyEndModal}
          showEditPageModal={state.showEditPageModal}
          showResponsivePreview={showResponsivePreview}
          showSEOAnalysis={showSEOAnalysis}
          showSuccessToast={state.showSuccessToast}
          successMessage={state.successMessage}
          // Loading states
          isSavingTemplate={state.isSavingTemplate}
          isAddingComponent={state.isAddingComponent}
          // Other states
          isInvisibleComponentMode={state.isInvisibleComponentMode}
          editingInvisibleComponent={state.editingInvisibleComponent}
          insertPosition={state.insertPosition}
          componentRefreshTrigger={state.componentRefreshTrigger}
          // Complex modal states
          duplicateModal={state.duplicateModal}
          deleteModal={state.deleteModal}
          libraryEditorModal={libraryEditorModal}
          // Handlers
          onCloseSaveTemplateModal={() => state.setShowSaveTemplateModal(false)}
          onSaveAsTemplate={handlers.handleSaveAsTemplate}
          onCloseComponentLibraryModal={() => {
            state.setShowComponentLibraryModal(false);
            state.setIsInvisibleComponentMode(false);
            state.setInsertPosition(null);
          }}
          onSelectTemplate={handlers.handleAddComponent}
          onPlaceCompeteForm={handlers.handlePlaceCompeteForm}
          onSelectLibraryComponent={handlers.handleAddLibraryComponent}
          onEditLibraryComponent={component => {
            state.setShowComponentLibraryModal(false);
            state.setIsInvisibleComponentMode(false);
            state.setInsertPosition(null);
            setLibraryEditorModal({ isOpen: true, component });
          }}
          onCloseLibraryEditorModal={() =>
            setLibraryEditorModal({ isOpen: false, component: null })
          }
          onLibrarySaveSuccess={() => {
            showToast('Library component updated successfully!', 'success', {
              showRoary: true,
              phraseKey: 'changes_saved',
            });
            state.setComponentRefreshTrigger(prev => prev + 1);
          }}
          onLibrarySyncSuccess={() => {
            showToast('Components synced successfully!', 'success', {
              showRoary: true,
              phraseKey: 'generic_success',
            });
            state.setComponentRefreshTrigger(prev => prev + 1);
          }}
          onCloseHeadContentModal={() => {
            state.setShowHeadContentModal(false);
            state.setEditingInvisibleComponent(null);
          }}
          onSaveHeadContent={handlers.handleSaveHeadContent}
          onCloseBodyEndModal={() => {
            state.setShowBodyEndModal(false);
            state.setEditingInvisibleComponent(null);
          }}
          onSaveBodyEnd={handlers.handleSaveBodyEnd}
          onCloseEditPageModal={() => state.setShowEditPageModal(false)}
          onPageUpdated={page => state.setCurrentPage(page)}
          onUnpublish={() => {
            state.setShowEditPageModal(false);
            handlers.handleUnpublish();
          }}
          onCloseDuplicateModal={() =>
            state.setDuplicateModal({ isOpen: false, type: '', comps: [] })
          }
          onDuplicateCleanup={handlers.handleDuplicateCleanup}
          onCloseDeleteModal={() => state.setDeleteModal({ isOpen: false, component: null })}
          onConfirmDelete={handlers.handleConfirmDelete}
          onCloseResponsivePreview={() => setShowResponsivePreview(false)}
          onCloseSEOAnalysis={() => setShowSEOAnalysis(false)}
          onSeoFixApplied={() => state.setComponentRefreshTrigger(prev => prev + 1)}
          onCloseSuccessToast={() => state.setShowSuccessToast(false)}
          setCurrentPage={state.setCurrentPage}
          setPageLayout={state.setPageLayout}
          showToast={showToast}
          onPublishPage={handlers.handlePublish}
          isPublishingPage={state.isPublishing}
          onRefetchPublishStatus={refetchPublishStatus}
        />

        {/* Media Library Modal */}
        <MediaLibraryModal
          isOpen={showMediaLibrary}
          onClose={() => setShowMediaLibrary(false)}
          siteId={siteId}
        />

        <GettingStartedHost sites={state.site ? [state.site] : undefined} />

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </ComponentEditDraftProvider>
  );
};

// Re-export for convenience
export default PageEditor;
